import type { CSSProperties } from "react";

// A few shared style objects for the HUD's recurring chrome. Colors are tokens;
// one-off layout stays inline in each component.
export const card: CSSProperties = {
  background: "var(--card)",
  border: "1px solid var(--line)",
  borderRadius: 10,
};

// Uppercase JetBrains-Mono micro-labels, the reference's "records" voice. Wide
// tracking, lighter weight than the old extrabold sans (mono reads heavy).
export const sectionLabel: CSSProperties = {
  fontFamily: "var(--mono)",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: ".08em",
  textTransform: "uppercase",
  color: "var(--mut)",
};

export const sectionMini: CSSProperties = {
  fontFamily: "var(--mono)",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: ".08em",
  textTransform: "uppercase",
  color: "var(--mut)",
};

// Tabular mono figures: spread onto money/stat numbers so amounts align in a
// column like a ledger. letterSpacing 0 overrides the display font's tightening.
export const figure: CSSProperties = {
  fontFamily: "var(--mono)",
  fontVariantNumeric: "tabular-nums",
  letterSpacing: 0,
};

export const stepper: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  background: "var(--card)",
  border: "1px solid var(--control-edge)",
  borderRadius: 999,
  padding: 4,
};

export const stepBtn: CSSProperties = {
  width: 28,
  height: 28,
  fontSize: 16,
  fontWeight: 700,
  color: "var(--sub)",
  background: "var(--raise)",
  border: "none",
  borderRadius: 999,
  cursor: "pointer",
};

// The mission-console world is matte: no glow shadows on bars or cards
// (portfolio DESIGN.md). The HUD keeps exactly one authored glow, the XP
// ring's breathing radial. bloom() stays as the single policy switch so the
// call sites don't churn if the policy ever changes.
export const bloom = (_color: string, _pct: string | number, _blur = 12): string | undefined => undefined;

// The fill inside a progress track. Every bar in the HUD goes through this.
//
// It animates `transform`, not `width`. A width transition relayouts on every
// frame of the animation, and the HUD has up to nine bars that can move at once
// (the domain-heat grid alone is eight), so that work lands on the main thread
// together. scaleX is composited: the browser hands the fill to the GPU and the
// rest of the page is never measured again.
//
// The span keeps its full width and its border-radius and is squashed
// horizontally, which is what preserves the rounded leading cap. Clipping the
// fill, or scaling a span with no radius, squares that edge off instead.
//
// Accepts a number or a "55%" string, since some callers hold percentages as
// pre-formatted display strings.
export const fill = (pct: string | number, secs = 1): CSSProperties => {
  const raw = typeof pct === "number" ? pct : parseFloat(String(pct));
  const n = Number.isFinite(raw) ? Math.max(0, Math.min(100, raw)) : 0;
  return {
    display: "block",
    height: "100%",
    width: "100%",
    transformOrigin: "left center",
    transform: `scaleX(${n / 100})`,
    transition: `transform ${secs}s cubic-bezier(.16,1,.3,1)`,
  };
};
